import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_LINKS } from '@/helpers/links';
import { prettyMoney } from '@scramble/utils';
import { prettyDate } from '@/helpers/date';
import { useGetLenderApplications } from '@/query/queries/fundraising/useGetLenderApplications';
import { FR_STEPS } from '@/helpers/fundraising';
import s from './index.module.scss';

export const LenderApplications = () => {
  const { t } = useTranslation();
  const { data } = useGetLenderApplications();
  const navigate = useNavigate();

  if (!data) return null;

  const { awaiting_lender, awaiting_borrower_guarantor, fundraising_summary, fundraising_result } = FR_STEPS;

  const getStepLabel = step => {
    switch (step) {
      case awaiting_lender:
        return t('fundraising:application_step_badge.awaiting_review');
      case awaiting_borrower_guarantor:
        return t('fundraising:application_step_badge.awaiting_confirmation');
      case fundraising_summary:
        return t('fundraising:application_step_badge.confirmed');
      case fundraising_result:
        return t('fundraising:application_step_badge.expired');
      default:
        return '';
    }
  };

  const grouped = data.reduce(
    (acc, item) => {
      const key = !item.step || [fundraising_result].includes(item.step) ? 'history' : 'active';
      acc[key].push({
        ...item,
        step_label: getStepLabel(item.step),
      });
      return acc;
    },
    {
      active: [],
      history: [],
    },
  );

  return (
    <div className={s.wrapper}>
      {!!grouped.active.length && (
        <div className={s.section}>
          <div className={classnames(s.title, 'f-article-accent')}>Active Applications</div>
          <div className={s.list}>
            {grouped.active.map(item => (
              <div className={s.card} key={item.fundraising_id}>
                <div className={s.top}>
                  <div className={s.brand}>
                    <img
                      src={item.company.logo?.file}
                      alt={item.company.name}
                      width={32}
                      height={32}
                    />
                    <span className="f-paragraph-accent">{item.company.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(APP_LINKS.fundraising, { state: { application: item } })
                    }
                    className={s.link}
                  >
                    View application
                  </button>
                </div>
                <div className={s.bottom}>
                  <div className={s.left}>
                    <div className="f-body-regular">{item.fundraising_name}</div>
                    <div className="f-body-accent">{prettyMoney(item.funds)}</div>
                  </div>
                  <div className={s.right}>
                    <div className={classnames(s.submitted, 'f-body-regular')}>
                      Submitted on {prettyDate(item.submitted_date)}
                    </div>
                    <div className={classnames(s.badge, item.step)}>{item.step_label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!!grouped.history.length && (
        <div className={s.section}>
          <div className={classnames(s.title, 'f-article-accent')}>Applications history</div>
          <div className={s.list}>
            {grouped.history.map(item => (
              <div className={s.card} key={item.fundraising_id}>
                <div className={s.top}>
                  <div className={s.brand}>
                    <img
                      src={item.company.logo?.file}
                      alt={item.company.name}
                      width={32}
                      height={32}
                    />
                    <span className="f-paragraph-accent">{item.company.name}</span>
                  </div>
                </div>
                <div className={s.bottom}>
                  <div className={s.left}>
                    <div className="f-body-regular">{item.fundraising_name}</div>
                    <div className="f-body-accent">{prettyMoney(item.funds)}</div>
                  </div>
                  <div className={s.right}>
                    <div className={classnames(s.submitted, 'f-body-regular')}>
                      Submitted on {prettyDate(item.submitted_date)}
                    </div>
                    <div className={classnames(s.badge, item.step)}>{item.step_label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};